import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AnalyticsParameter } from '../../models/analytics-parameter';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material';
import { ReferenceOptions } from './reference-options';
import { ReferenceAutoComplete } from './reference-auto-complete';
import { ControlContainer, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseAutoCompleteService } from '../../../../services/base-auto-complete.service';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { IBaseEnumEntity } from '../../../../base/interfaces/IBaseEnumEntity';
import { fromEvent } from 'rxjs';
import { getAttributeError } from './parameter-validators';

@Component({
  selector: 'app-analytics-reference',
  templateUrl: './analytics-reference.component.html',
  styleUrls: ['./analytics-reference.component.css']
})
export class AnalyticsReferenceComponent implements OnInit {
  
  @Input() parameter: AnalyticsParameter;
  @Input() useMandatory = true;
  @Input() allowManual = false;
  @Input() disabled = false;

  isDisabled = false;
  private options: ReferenceOptions;
  @ViewChild('refAuto', {static: false}) autocompleteRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger, {static: false}) autocompleteTrigger: MatAutocompleteTrigger;
  public form: FormGroup;
  public isMultiLine = false;
  public isLoading = false;
  public indicatorDiameter = 18;
  public filteredOptions: ReferenceAutoComplete[];
  private currentValue: string;
  public searchCtrl: FormControl = new FormControl(null);
  private skip = 0;
  private take = 5; 
  constructor(
    private controlContainer: ControlContainer,
    private autoCompleteService: BaseAutoCompleteService
  ) { }
  ngOnInit() {
    this.form = <FormGroup>this.controlContainer.control;    
    const value = this.form.get('value');
    this.searchCtrl.setValue(value.value);
    value.valueChanges.subscribe((newValue:string) => {
      this.searchCtrl.setValue(newValue);
    });
    this.isDisabled = value.disabled;
    this.searchCtrl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe( (newValue: string) => {
      this.skip = 0;
      this.onFilter(newValue);
    });
    if (this.useMandatory && this.parameter.mandatory) {
      value.setValidators([Validators.required]);
    }    
  }
  private getAttributes() : Map<string, string> {
    const ret = new Map<string, string>();
    if(this.form && this.form.parent){
      if(this.form.parent  instanceof FormArray) { // check if we are in the attribute list
        const attributes = <FormArray>this.form.parent;
        for(let c=0; c<attributes.controls.length;c++){
          let control = <FormGroup>attributes.controls[c];
          let id = control.get('id').value;
          let value = control.get('value').value;
          if(id && value){
            ret.set(id, value);
          }
        }
        if(attributes.root instanceof FormGroup){ // if we have the root group
          const rootForm = <FormGroup>attributes.root;
          let name = null;
          let typeId = null;
          let documentId = null;
          if(rootForm.get('name')) {
            name = rootForm.get('name').value
          }
          if(rootForm.get('typeId')) {
            typeId = rootForm.get('typeId').value
          }
          if(rootForm.get('id')) {
            documentId = rootForm.get('id').value
          }
          if(name){
            ret.set('name',name);
          }
          if(typeId){
            ret.set('typeId',typeId);
          }                    
          if(documentId){
            ret.set('documentId',documentId);
          }                              
        }
      }
    }
    return ret;
  }
  displayFn = (id: string) => {
    const value = this.form.get('value');    
    const displayValue = this.form.get('displayValue');
    if (!id || !value || !displayValue) {
      return null;
    } else if (value.value === id) {
      return displayValue.value;
    } else {
      if (this.filteredOptions) {
        const option = this.filteredOptions.find(f => f.id === id);
        if (option) {
          return option.name;
        } else {
          return null;
        }
      } else {
        return null;
      }
    }
  }
  getError(): string {
    const error = this.form.get('error').value;
    if (error && this.parameter.id) {
      return getAttributeError(this.parameter.id, error);
    }
    else {
      return null;
    }
  }  
  onFilter = (value: string) => {
    this.currentValue = value;
    //this.documentAttribute.referenceInfo.apiPath
    this.isLoading = true;

    this.autoCompleteService.Query(this.parameter.queryId,value, this.skip, this.take, this.getAttributes()).subscribe(data => {
      this.isLoading = false;
      let defaultValue:ReferenceAutoComplete = null;      
      if(this.parameter){
        if(this.parameter && this.parameter.mandatory && this.searchCtrl.value){
          const s = this.searchCtrl.value;
          defaultValue = new ReferenceAutoComplete();
          defaultValue.id = this.getDefaultId(s);
          defaultValue.displayName = s
          defaultValue.name = s
        }
      }
      if (data) {
        let options = [];
        if(defaultValue){
          options.push(defaultValue);
        }
        if(this.skip === 0){
          options = [...options, ...data.payload];
          //this.filteredOptions = data.payload;
        }
        else {
          options = [...this.filteredOptions, ...data.payload];
          //this.filteredOptions = [...this.filteredOptions, ...data.payload]
        }
        this.filteredOptions = options;        
      }
    }, error => {
      this.isLoading = false;
    });
  }
  getDefaultId(name:string){
    let ret = name;
    return ret;
  }
  onSelected = (optionValue: IBaseEnumEntity | string) => {
    console.log('onSelected', optionValue);
    const value = this.form.get('value');
    const displayValue = this.form.get('displayValue');
    let option = null;        
    if (typeof optionValue === 'string' || optionValue instanceof String) {
      if (this.filteredOptions) {
        option = this.filteredOptions.find(f => f.id === optionValue);
      } else {
        option = option;
      }
    }
    if (option) {
      value.setValue(option.id);
      displayValue.setValue(option.displayName);
    } else {
      value.setValue(null,{onlySelf:false, emitEvent:true});
      displayValue.setValue(null);
    }
  }
  clearSearchField(evt: MouseEvent) {
    const value = this.form.get('value');    
    const name = this.form.get('name');
    const displayValue = this.form.get('displayValue');
    value.setValue(null);
    displayValue.setValue(null);
    this.searchCtrl.setValue(null);
    if (name) {
      name.setValue(null);
    }    
    evt.stopPropagation();
  }
  refreshData() {
    this.onFilter(this.currentValue);
  }
  getEnumValue(value: IBaseEnumEntity, options: string | string[]) {
    let ret = null;
    if (value) {
      if (options instanceof Array) {
        ret = '';
        options.forEach((option, i) => {
          const v = value[option];
          if (v) {
            if (i > 0) {
              ret += ' ';
            }
            ret += `${v}`;
          }
        });
      } else {
        ret = value[options];
      }
    }
    return ret;
  }
  setLineHeight(value: IBaseEnumEntity) {
    let ret = 0.8;
    if (this.options.title) {
      ret += 0.6;
    }
    if (this.options.subTitle) {
      ret += 0.5;
    }
    if (this.options.detail) {
      ret += 0.3;
    }
    return `${ret}em`;
  }
  onFocus() {
    const serach = this.form.get('value').value;
    this.skip = 0;
    this.onFilter(serach);
  }
  autocompleteScroll() {    
    setTimeout(() => {
      if (
        this.autocompleteRef &&
        this.autocompleteTrigger &&
        this.autocompleteRef.panel
      ) {
        fromEvent(this.autocompleteRef.panel.nativeElement, 'scroll')
          .pipe(
            map(x => this.autocompleteRef.panel.nativeElement.scrollTop),
            takeUntil(this.autocompleteTrigger.panelClosingActions)
          )
          .subscribe(x => {
            const scrollTop = this.autocompleteRef.panel.nativeElement
              .scrollTop;
            const scrollHeight = this.autocompleteRef.panel.nativeElement
              .scrollHeight;
            const elementHeight = this.autocompleteRef.panel.nativeElement
              .clientHeight;
              const atBottom = scrollHeight === scrollTop + elementHeight;
            if (atBottom) {
              this.skip += this.take
              this.onFilter(this.searchCtrl.value);
            }
          });
      }
    });
  }  
}
